/**
 * Shell — authenticated app chrome (2026-09 redesign, Remote + Stripe):
 *
 *   ┌───────────────────────────────┬──────────┐
 *   │ top bar: link light · ⌘K · 🔔  │          │
 *   ├───────────────────────────────┤ sidebar  │  (RTL: sidebar on the right)
 *   │ page                           │ groups   │
 *   │                                │ user ▾   │
 *   └───────────────────────────────┴──────────┘
 *
 * The sidebar is white, full height, grouped by task (components/layout/
 * nav.ts), with the open-alert count on "Alerts" and the account at the
 * bottom. The top bar carries the three things that must be visible from
 * EVERY page: whether the cameras are connected (a cellular uplink dropping
 * is the product's one silent failure), quick search, and the alert bell.
 *
 * On phones (the Capacitor APK is a first-class field client) the sidebar
 * becomes an off-canvas drawer behind a menu button.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/hooks/useDir";
import { useSite, siteDisplayName } from "@/hooks/useSite";
import { useCameras } from "@/hooks/useCameras";
import { useDashboard } from "@/hooks/useDashboard";
import { summarizeLinks, downMinutes } from "@/lib/cameraHealth";
import { cameraDisplayName } from "@/components/cameras/cameraName";
import { fmtNum } from "@/lib/format";
import { LanguageSwitcher } from "@/components/common/LanguageSwitcher";
import { Avatar } from "@/components/common/Avatar";
import {
  IconAlerts,
  IconChevronDown,
  IconClose,
  IconLogout,
  IconMenu,
  IconSearch,
  NAV_ICON_MAP,
} from "@/components/common/icons";
import { NAV_GROUPS } from "./nav";
import { CommandPalette, useCommandPaletteHotkey } from "./CommandPalette";

/** Same key `useNativeFcm` writes after a successful registration. */
const FCM_LAST_TOKEN_KEY = "warnix_v2_fcm_last_token";

function Brand() {
  const { t } = useTranslation();
  const lang = useLang();
  const { data: site } = useSite();
  const name = siteDisplayName(site, lang);
  return (
    <Link
      to="/dashboard"
      className="flex items-center gap-2.5 px-3 py-1 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
    >
      <span
        aria-hidden="true"
        className="w-8 h-8 shrink-0 rounded-lg bg-brand-500 text-white flex items-center justify-center text-[15px] font-bold shadow-[inset_0_-1px_0_rgb(0_0_0/0.15)]"
      >
        W
      </span>
      <span className="min-w-0">
        <span className="block text-[15px] font-bold text-slate-900 leading-5">WarnixAI</span>
        <span className="block text-xs text-slate-500 truncate leading-4">{name || t("shell.tagline")}</span>
      </span>
    </Link>
  );
}

function NavList({ isAdmin, openAlerts, onNavigate }: { isAdmin: boolean; openAlerts: number; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const main = NAV_GROUPS.filter((g) => g.key !== "system");
  return (
    <div className="space-y-5">
      {main.map((group) => {
        const visible = group.items.filter((item) => !item.adminOnly || isAdmin);
        if (visible.length === 0) return null;
        return (
          <div key={group.key}>
            <div className="px-3 pb-1.5 text-xs font-medium text-slate-500">{t(`nav.groups.${group.key}`)}</div>
            <ul className="space-y-0.5">
              {visible.map((item) => (
                <NavRow key={item.to} to={item.to} navKey={item.key} onNavigate={onNavigate}
                  badge={item.key === "alerts" && openAlerts > 0 ? openAlerts : undefined} />
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

function NavRow({ to, navKey, badge, onNavigate }: { to: string; navKey: string; badge?: number; onNavigate?: () => void }) {
  const { t } = useTranslation();
  const Icon = NAV_ICON_MAP[navKey];
  return (
    <li>
      <NavLink
        to={to}
        onClick={onNavigate}
        className={({ isActive }) =>
          `flex items-center gap-3 h-9 px-3 rounded-lg text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
            isActive ? "bg-brand-50 text-brand-700 font-semibold" : "text-slate-700 font-medium hover:bg-slate-100"
          }`
        }
      >
        {Icon && <Icon className="w-5 h-5 shrink-0" />}
        <span className="flex-1 min-w-0 truncate">{t(`nav.${navKey}`)}</span>
        {badge !== undefined && (
          <span className="min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[11px] font-semibold flex items-center justify-center tabular-nums">
            {badge > 99 ? "99+" : fmtNum(badge)}
          </span>
        )}
      </NavLink>
    </li>
  );
}

function SystemNav({ isAdmin, onNavigate }: { isAdmin: boolean; onNavigate?: () => void }) {
  const system = NAV_GROUPS.find((g) => g.key === "system");
  if (!system) return null;
  return (
    <ul className="space-y-0.5">
      {system.items
        .filter((item) => !item.adminOnly || isAdmin)
        .map((item) => (
          <NavRow key={item.to} to={item.to} navKey={item.key} onNavigate={onNavigate} />
        ))}
    </ul>
  );
}

function UserMenu({ onLogout }: { onLogout: () => void }) {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-slate-100 text-start focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
      >
        <Avatar name={user?.email} size={32} />
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-semibold text-slate-900 truncate">{user?.email}</span>
          <span className="block text-xs text-slate-500">{user ? t(`shell.roles.${user.role}`) : ""}</span>
        </span>
        <IconChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
      </button>
      {open && (
        <div className="absolute bottom-full mb-2 inset-x-0 bg-white rounded-xl shadow-pop p-2 z-10">
          <div className="px-2 py-1.5">
            <LanguageSwitcher variant="full" />
          </div>
          <div className="my-1 border-t border-slate-100" />
          <button
            type="button"
            onClick={onLogout}
            className="w-full flex items-center gap-2 h-9 px-2 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            <IconLogout className="w-[18px] h-[18px] text-slate-500" />
            {t("shell.logout")}
          </button>
        </div>
      )}
    </div>
  );
}

function SidebarBody({
  isAdmin,
  openAlerts,
  onNavigate,
  onLogout,
}: {
  isAdmin: boolean;
  openAlerts: number;
  onNavigate?: () => void;
  onLogout: () => void;
}) {
  return (
    <div className="flex flex-col h-full">
      <div className="px-3 pt-4 pb-5">
        <Brand />
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        <NavList isAdmin={isAdmin} openAlerts={openAlerts} onNavigate={onNavigate} />
      </nav>
      <div className="px-3 pt-3 pb-3 border-t border-slate-200 space-y-2">
        <SystemNav isAdmin={isAdmin} onNavigate={onNavigate} />
        <UserMenu onLogout={onLogout} />
      </div>
    </div>
  );
}

/** Green "all cameras connected" / amber "Camera 2 offline · 4 min" light. */
function LinkLight() {
  const { t } = useTranslation();
  const lang = useLang();
  const { data: cameras } = useCameras();
  const { total, down } = summarizeLinks(cameras);
  if (total === 0) return null;
  let text: string;
  if (down.length === 0) text = t("shell.link.allOnline");
  else if (down.length === 1) {
    const mins = downMinutes(down[0]);
    const name = cameraDisplayName(down[0], lang);
    text = mins ? t("shell.link.oneOfflineFor", { name, n: fmtNum(mins) }) : t("shell.link.oneOffline", { name });
  } else text = t("shell.link.manyOffline", { n: fmtNum(down.length) });
  const bad = down.length > 0;
  return (
    <Link
      to="/cameras"
      className={`inline-flex items-center gap-2 h-8 px-3 rounded-full text-[13px] font-medium truncate max-w-full ${
        bad ? "bg-tag-warn-bg text-tag-warn-fg" : "bg-tag-ok-bg text-tag-ok-fg"
      }`}
    >
      <span className={`w-2 h-2 rounded-full shrink-0 ${bad ? "bg-amber-500" : "bg-emerald-500"}`} aria-hidden="true" />
      <span className="truncate">{text}</span>
    </Link>
  );
}

function TopBar({ onMenu, onSearch, openAlerts }: { onMenu: () => void; onSearch: () => void; openAlerts: number }) {
  const { t } = useTranslation();
  const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);
  return (
    <header className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
      <div className="h-14 px-4 lg:px-8 flex items-center gap-3">
        <button
          type="button"
          onClick={onMenu}
          aria-label={t("shell.openMenu")}
          className="md:hidden w-9 h-9 -ms-2 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <IconMenu className="w-5 h-5" />
        </button>
        <div className="min-w-0 flex-1 flex">
          <LinkLight />
        </div>
        <button
          type="button"
          onClick={onSearch}
          className="hidden sm:flex items-center gap-2 h-9 w-72 max-w-[40vw] px-3 rounded-lg border border-slate-200 bg-slate-50 text-sm text-slate-500 hover:border-slate-300 hover:bg-white transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <IconSearch className="w-4 h-4 shrink-0" />
          <span className="flex-1 text-start truncate">{t("shell.searchPlaceholder")}</span>
          <kbd className="text-[11px] font-medium text-slate-500 border border-slate-200 bg-white rounded px-1.5">
            {isMac ? "⌘K" : "Ctrl K"}
          </kbd>
        </button>
        <button
          type="button"
          onClick={onSearch}
          aria-label={t("shell.search")}
          className="sm:hidden w-9 h-9 rounded-lg text-slate-600 hover:bg-slate-100 flex items-center justify-center"
        >
          <IconSearch className="w-5 h-5" />
        </button>
        <Link
          to="/alerts?status=open"
          aria-label={t("shell.openAlerts", { n: fmtNum(openAlerts) })}
          className="relative w-9 h-9 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
        >
          <IconAlerts className="w-5 h-5" />
          {openAlerts > 0 && (
            <span className="absolute -top-1.5 -end-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center tabular-nums ring-2 ring-white">
              {openAlerts > 99 ? "99+" : fmtNum(openAlerts)}
            </span>
          )}
        </Link>
      </div>
    </header>
  );
}

export default function Shell() {
  const { t } = useTranslation();
  const user = useAuth((s) => s.user);
  const logout = useAuth((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const { data: dash } = useDashboard();
  // Today's open alerts (migration 028). Not the 7-day unacknowledged count:
  // no historical alert was ever acknowledged, so that one is permanently
  // "99+" and says nothing. Older servers without `open_today` fall back.
  // (types/dashboard.ts belongs to the web-ops stream; read the optional
  // field structurally until that type lands.)
  const openToday = (dash as { open_today?: number | null } | undefined)?.open_today;
  const openAlerts = openToday ?? dash?.unacknowledged_count ?? 0;

  const openPalette = useCallback(() => setPaletteOpen(true), []);
  useCommandPaletteHotkey(openPalette);

  // Close the mobile drawer whenever the route changes.
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    // C1-6 — drop this device's push token before the session ends so a
    // shared phone stops receiving the previous user's alerts. Best effort:
    // the logout itself never waits on the network more than a moment.
    let fcmToken: string | null = null;
    try {
      fcmToken = localStorage.getItem(FCM_LAST_TOKEN_KEY);
    } catch {
      fcmToken = null;
    }
    if (fcmToken) {
      try {
        await Promise.race([
          api<void>(`/api/fcm/register?token=${encodeURIComponent(fcmToken)}`, { method: "DELETE" }),
          new Promise<void>((resolve) => setTimeout(resolve, 1500)),
        ]);
      } catch {
        /* offline or already gone — nothing to do */
      }
      try {
        localStorage.removeItem(FCM_LAST_TOKEN_KEY);
      } catch {
        /* ignore */
      }
    }
    logout();
    navigate("/login", { replace: true });
  };

  const isAdmin = user?.role === "admin";

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 md:flex">
      {/* Desktop sidebar — sits at the inline START (right in Hebrew). */}
      <aside className="hidden md:block w-[248px] shrink-0 bg-white border-e border-slate-200 sticky top-0 h-screen">
        <SidebarBody isAdmin={isAdmin} openAlerts={openAlerts} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer + backdrop */}
      {menuOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label={t("shell.navMenu")}>
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setMenuOpen(false)} />
          <div className="absolute inset-y-0 start-0 w-[280px] max-w-[85%] bg-white shadow-soft">
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              aria-label={t("common.close")}
              className="absolute top-4 end-3 w-8 h-8 rounded-lg text-slate-500 hover:bg-slate-100 flex items-center justify-center z-10"
            >
              <IconClose className="w-4 h-4" />
            </button>
            <SidebarBody
              isAdmin={isAdmin}
              openAlerts={openAlerts}
              onNavigate={() => setMenuOpen(false)}
              onLogout={handleLogout}
            />
          </div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col min-h-screen">
        <TopBar onMenu={() => setMenuOpen(true)} onSearch={openPalette} openAlerts={openAlerts} />
        <main className="flex-1 w-full max-w-[1320px] mx-auto px-4 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </main>
      </div>

      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
